import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { useRef } from "react";
import {
  FaBolt,
  FaFireExtinguisher,
  FaHardHat,
  FaNetworkWired,
} from "react-icons/fa";

import service1 from "../assets/HFour.webp";
import service3 from "../assets/HThree.jpg";
import service2 from "../assets/iotfour.jpg";
import service4 from "../assets/FireTwo.jpg";

const services = [
  {
    icon: <FaHardHat className="text-orange-600 text-xl mb-2" />,
    title: "Construction & Civil Works",
    description:
      "We design and deliver modern infrastructure projects with precision and professionalism—from building structures to roadworks and civil engineering services.",
    image: service1,
    link: "/construction",
  },
  {
    icon: <FaNetworkWired className="text-orange-600 text-xl mb-2" />,
    title: "ICT & IoT Solutions",
    description:
      "Our ICT and IoT services integrate smart technologies to streamline operations, improve connectivity, and enhance system automation.",
    image: service2,
    link: "/ict",
  },
  {
    icon: <FaBolt className="text-orange-600 text-xl mb-2" />,
    title: "Electrical Installations",
    description:
      "We provide professional electrical design, wiring, panel installations, and maintenance—ensuring safety, compliance, and power reliability.",
    image: service3,
    link: "/electrical",
  },
  {
    icon: <FaFireExtinguisher className="text-orange-600 text-xl mb-2" />,
    title: "Fire Protection Systems",
    description:
      "From fire alarms to suppression systems, we implement complete fire safety solutions that protect lives and assets in all environments.",
    image: service4,
    link: "/fire",
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const sectionVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: "easeInOut" },
  },
};

const ServicesSection = () => {
  const ref = useRef();
  const isInView = useInView(ref, { once: false, threshold: 0.2 });

  return (
    <motion.section
      ref={ref}
      className="py-16 mb-36 w-full h-auto px-4 sm:px-6 lg:px-16 bg-white"
      variants={sectionVariant}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="max-w-6xl mx-auto flex flex-col items-center">
        {/* Section Heading */}
        <div className="text-center mb-10">
          <p className="text-xs sm:text-sm text-orange-600 font-semibold uppercase">
            What We Do
          </p>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
            Trusted Engineering & Technology Services
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 w-full">
          {services.map((service, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={cardVariant}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="flex flex-col relative items-center"
            >
              {/* Image */}
              <div className="relative w-full">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-[110%] h-36 z-10">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover rounded-lg shadow-md"
                  />
                </div>
              </div>

              {/* Text Card */}
              <div className="flex flex-col relative z-10 justify-between flex-grow mt-28 bg-white rounded-lg shadow border  px-4 py-6 text-start w-full">
                <div>
                  {service.icon}
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-1 text-gray-800">
                    {service.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="mt-auto">
                  <Link
                    to={service.link}
                    className="inline-block mt-3 px-3 py-2 text-xs sm:text-sm bg-orange-600 text-white rounded-lg 
                      hover:bg-white hover:text-orange-600 border hover:border-orange-600 transition"
                  >
                    Explore More →
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default ServicesSection;
